import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Search, Menu, X, LogOut, ShieldCheck, Crown } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/browse", label: "Browse" },
    { to: "/search", label: "Search" },
    { to: "/mylist", label: "My List" },
  ];

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#030303]/70 border-b border-white/10">
      <nav className="max-w-[1600px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="nav-logo" className="font-heading font-extrabold text-xl sm:text-2xl tracking-tighter text-white">
            POWER<span className="text-[#00F0FF] text-glow">2000</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                className={`text-sm font-medium transition-colors ${isActive(l.to) ? "text-[#00F0FF]" : "text-white/70 hover:text-white"}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/search")}
            data-testid="nav-search-btn"
            className="p-2 rounded-full text-white/80 hover:text-[#00F0FF] transition-colors"
            aria-label="Search"
          >
            <Search size={20} />
          </button>

          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-[#00F0FF] transition-colors">
              <ShieldCheck size={16} /> Studio
            </Link>
          )}

          {user && user.subscribed ? (
            <span data-testid="nav-premium-badge" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#00F0FF] border border-[#00F0FF]/40 rounded-full px-3 py-1">
              <Crown size={14} /> Premium
            </span>
          ) : (
            <Link to="/premium" data-testid="nav-premium" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-black bg-[#00F0FF] rounded-full px-3 py-1.5 hover:scale-105 transition-transform">
              <Crown size={14} /> Go Premium
            </Link>
          )}

          {user ? (
            <div className="hidden md:flex items-center gap-3">
              <span className="text-sm text-white/60 max-w-[120px] truncate">{user.name}</span>
              <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-full text-white/70 hover:text-[#FF2A2A] transition-colors" aria-label="Logout">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden md:inline-flex rounded-full bg-[#00F0FF] text-black text-sm font-semibold px-5 py-2 hover:scale-105 transition-transform"
            >
              Sign In
            </Link>
          )}

          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="md:hidden p-2 text-white" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#030303]/95 backdrop-blur-2xl px-4 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={`py-3 px-2 rounded-lg text-base ${isActive(l.to) ? "text-[#00F0FF] bg-white/5" : "text-white/80"}`}>
              {l.label}
            </Link>
          ))}
          {user && user.role === "admin" && (
            <Link to="/admin" onClick={() => setOpen(false)} className="py-3 px-2 text-white/80">Owner Studio</Link>
          )}
          {!(user && user.subscribed) && (
            <Link to="/premium" onClick={() => setOpen(false)} className="py-3 px-2 text-[#00F0FF] font-medium">Go Premium — $3.99/mo</Link>
          )}
          {user && user.subscribed && (
            <span className="py-3 px-2 text-[#00F0FF] font-medium">Premium active ✓</span>
          )}
          {user ? (
            <button onClick={() => { logout(); setOpen(false); }} className="py-3 px-2 text-left text-[#FF2A2A]">Log Out</button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="mt-2 rounded-full bg-[#00F0FF] text-black text-center font-semibold py-3">Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
};
